import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { getAssistant } from "../../features/assistant/assistantSlice";
import {
  upsertSetting,
  fetchSettingDetailsByUserId,
  fetchSettingById,
} from "../../features/settings/settingsSlice";
import WorkspaceProject from "./WorkspaceProject";
import { fetchPlatformConfigurations } from "../../features/platforms/platformsAPI";
import { fetchNotionDatabases } from "../../features/oauth/oauthAPI";
import {
  buildSettingPayload,
  PLATFORM_OPTIONS,
  PLATFORM_SETTINGS_ON_LOAD,
  PLATFORM_SETTINGS_ON_PROJECT_SELECT,
  WORKSPACE_FETCHERS,
  ITEMS_FETCHERS,
  normalizeDefaultSettings,
  isThreeStepPlatform,
  loadThirdStepItems,
} from "./PlatformWorkspace.utils";
import { PLATFORMS } from "../../constants/constants";

export const mergeDefaultSettings = (saved, edited) => {
  const editedMap = new Map(edited.map((s) => [s.name, s]));
  const savedMap = new Map(saved.map((s) => [s.name, s]));
  const allKeys = new Set([...savedMap.keys(), ...editedMap.keys()]);
  return Array.from(allKeys).map(
    (key) => editedMap.get(key) || savedMap.get(key),
  );
};

function PlatformWorkspace() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { assistantId, settingId } = useParams();
  const isUpdateMode = location.pathname.endsWith("/update");

  const { setting: reduxSetting } = useSelector((state) => state.setting);
  const { setting: initialSetting, assistant: initialAssistant } =
    location.state || {};

  const [setting, setSetting] = useState(initialSetting);
  const [assistant, setAssistant] = useState(initialAssistant);
  const [platformName, setPlatformName] = useState("");

  const [loading, setLoading] = useState(true);
  const [workspaces, setWorkspaces] = useState([]);
  const [projects, setProjects] = useState([]);
  const [items, setItems] = useState([]);
  const [advancedSettings, setAdvancedSettings] = useState(null);
  const [advancedSettingsLoading, setAdvancedSettingsLoading] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const { user } = useSelector((state) => state.user);

  const [selectedDefaultSettings, setSelectedDefaultSettings] = useState(() =>
    normalizeDefaultSettings(initialSetting?.defaultSettings),
  );
  const [selectedWorkspace, setSelectedWorkspace] = useState(
    initialSetting?.integrationIds?.id1 || "",
  );
  const [selectedProject, setSelectedProject] = useState(
    initialSetting?.integrationIds?.id2 || "",
  );
  const [selectedItem, setSelectedItem] = useState(
    initialSetting?.integrationIds?.id3 ||
      initialSetting?.integrationIds?.id2 ||
      "",
  );
  const platformCfg = PLATFORM_OPTIONS[platformName] || { preSteps: [] };
  const isDatabaseOnly = platformCfg.isDatabaseOnly || false;

  useEffect(() => {
    if (!isUpdateMode || !setting?.integrationIds) return;
    const { id1_metadata, id2_metadata, id3_metadata } = setting.integrationIds;
    if (platformCfg.itemsOnLoad) {
      if (id1_metadata) {
        setItems((prev) => (prev.length ? prev : [id1_metadata]));
        setSelectedItem(String(id1_metadata.id));
      }
      return;
    }
    if (isThreeStepPlatform(platformName)) {
      if (id1_metadata) {
        setWorkspaces([id1_metadata]);
        setSelectedWorkspace(String(id1_metadata.id));
      }
      if (id2_metadata) {
        setProjects([id2_metadata]);
        setSelectedProject(String(id2_metadata.id));
      }
      if (id3_metadata) {
        setItems([id3_metadata]);
        setSelectedItem(String(id3_metadata.id));
      }
      return;
    }

    if (id1_metadata) setWorkspaces([id1_metadata]);
    if (id2_metadata) setItems([id2_metadata]);
  }, [isUpdateMode, setting, platformName, platformCfg.itemsOnLoad]);

  useEffect(() => {
    if (reduxSetting) {
      setSetting(reduxSetting);
      if (isUpdateMode) {
        setSelectedDefaultSettings(
          normalizeDefaultSettings(reduxSetting?.defaultSettings),
        );
      }
    }
  }, [reduxSetting, isUpdateMode]);

  useEffect(() => {
    const loadAssistant = async () => {
      try {
        let data = initialAssistant;
        if (!data) {
          const action = await dispatch(getAssistant(assistantId));
          data = action.payload;
        }
        if (settingId) {
          await dispatch(fetchSettingById({ settingUuid: settingId }));
        }
        setAssistant(data);
        setPlatformName(data?.platform?.type?.toLowerCase() || "");
        setLoading(false);
      } catch {
        setLoading(false);
      }
    };
    if (assistantId) loadAssistant();
  }, [assistantId, dispatch, initialAssistant, settingId]);

  useEffect(() => {
    if (isUpdateMode) {
      setLoading(false);
      return;
    }
    if (!setting?.uuid || !platformName) {
      setLoading(true);
      return;
    }

    const loadWorkspaces = async () => {
      try {
        setLoading(true);

        if (isDatabaseOnly) {
          const response = await fetchNotionDatabases(setting.uuid);
          setItems(response?.data || []);
          return;
        }

        if (platformCfg.itemsOnLoad) {
          const response = await ITEMS_FETCHERS[platformName]?.(setting.uuid);
          setItems(response?.data || []);
          return;
        }

        const workspaceFetcher = WORKSPACE_FETCHERS[platformName];
        if (!workspaceFetcher) return;

        const response = await workspaceFetcher(setting.uuid, assistantId);
        const ws = response?.data || [];
        setWorkspaces(ws);

        if (platformCfg.fallbackToItems && ws.length === 0) {
          const fallback = await ITEMS_FETCHERS[platformName]?.(setting.uuid);
          setItems(fallback?.data || []);
        }
      } catch (err) {
        console.error("Workspace load error", err);
      } finally {
        setLoading(false);
      }
    };

    loadWorkspaces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformName, setting?.uuid, assistantId, isUpdateMode, isDatabaseOnly]);

  useEffect(() => {
    if (!platformName || !setting?.uuid || !assistant?.platform?.uuid) return;

    if (isUpdateMode) {
      loadAdvancedSettings(
        setting?.integrationIds?.id1,
        setting?.integrationIds?.id2,
      );
      return;
    }

    if (PLATFORM_SETTINGS_ON_LOAD.includes(platformName)) {
      loadAdvancedSettings(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformName, setting?.uuid, assistant?.platform?.uuid, isUpdateMode]);

  const fetchItems = async (workspaceId) => {
    const itemFetcher = ITEMS_FETCHERS[platformName];

    if (!itemFetcher) return;

    try {
      const response = await itemFetcher(setting.uuid, workspaceId);

      if (isThreeStepPlatform(platformName)) {
        setProjects(response?.data || []);
      } else {
        setItems(response?.data || []);
      }
    } catch (err) {
      console.error("Items load error", err);
    }
  };

  const loadAdvancedSettings = async (workspaceGid, projectGid) => {
    try {
      setAdvancedSettingsLoading(true);
      const data = await fetchPlatformConfigurations({
        settingId: setting?.uuid,
        workspaceGid,
        projectGid,
      });
      setAdvancedSettings(data?.fieldData ?? data);
    } catch (error) {
      console.error("Error fetching advanced settings:", error);
      setAdvancedSettings(null);
    } finally {
      setAdvancedSettingsLoading(false);
    }
  };

  const handleWorkspaceChange = async (id) => {
    setSelectedWorkspace(id);
    setSelectedItem("");
    setItems([]);

    if (isThreeStepPlatform(platformName)) {
      setSelectedProject("");
      setProjects([]);
    }

    setAdvancedSettings(null);
    setProjectsLoading(true);
    try {
      await fetchItems(id);
    } finally {
      setProjectsLoading(false);
    }
  };
  const handleProjectChange = async (projectId) => {
    setSelectedProject(projectId);
    setSelectedItem("");
    setItems([]);

    setProjectsLoading(true);

    try {
      if (isThreeStepPlatform(platformName)) {
        const data = await loadThirdStepItems({
          platformName,
          settingUuid: setting.uuid,
          workspaceId: selectedWorkspace,
          projectId,
          projects,
        });

        setItems(data);

        return data.length > 0;
      }

      return true;
    } finally {
      setProjectsLoading(false);
    }
  };

  const handleItemChange = async (itemId, shouldLoadSettings = true) => {
    setSelectedItem(itemId);

    if (isDatabaseOnly && !shouldLoadSettings) return;

    if (PLATFORM_SETTINGS_ON_PROJECT_SELECT.includes(platformName)) {
      if (isThreeStepPlatform(platformName)) {
        const selectedProjectData = projects.find(
          (project) => String(project.id) === String(selectedProject),
        );

        if (platformName === PLATFORMS.JIRA?.toLowerCase()) {
          await loadAdvancedSettings(
            selectedProjectData?.workspaceId,
            selectedProjectData?.key,
          );
        } else {
          await loadAdvancedSettings(selectedWorkspace, selectedProject);
        }
        return;
      }
      await loadAdvancedSettings(selectedWorkspace, itemId);
    }
  };

  // const handleSkip = async () => {
  //   try {
  //     const integrationIds = {
  //       ...(setting?.integrationIds || {}),
  //       id1: "",
  //       id2: "",
  //     };
  //     const updatedSetting = await dispatch(
  //       upsertSetting({ uuid: assistantId, data: { integrationIds } }),
  //     );
  //     navigate(`/apps/${assistantId}`, {
  //       state: { assistant, setting: updatedSetting, timestamp: Date.now() },
  //     });
  //   } catch (error) {
  //     console.error("Error skipping workspace selection:", error);
  //   }
  // };

  const handleContinue = async () => {
    try {
      const data = buildSettingPayload({
        setting,
        selectedWorkspace,
        selectedProject,
        selectedItem,
        workspaces,
        projects,
        items,
        selectedDefaultSettings,
        isUpdateMode,
        mergeDefaultSettings,
        settingId,
        platformName,
      });

      await dispatch(upsertSetting({ uuid: assistantId, data }));

      const completeSetting = await dispatch(
        fetchSettingDetailsByUserId({ accountId: user?.org }),
      );

      navigate(`/apps/installed/${assistantId}/setting/${settingId}`, {
        state: { assistant, setting: completeSetting, timestamp: Date.now() },
      });
    } catch (error) {
      console.error("Error updating setting:", error);
    }
  };

  return (
    <WorkspaceProject
      platform={platformName}
      workspaces={workspaces}
      projects={projects}
      items={items}
      selectedWorkspace={selectedWorkspace}
      selectedProject={selectedProject}
      selectedItem={selectedItem}
      handleWorkspaceChange={handleWorkspaceChange}
      handleProjectChange={handleProjectChange}
      setSelectedItem={handleItemChange}
      handleContinue={handleContinue}
      // handleSkip={handleSkip}
      loading={loading}
      projectsLoading={projectsLoading}
      setting={setting}
      advancedSettings={advancedSettings}
      advancedSettingsLoading={advancedSettingsLoading}
      selectedDefaultSettings={selectedDefaultSettings}
      setSelectedDefaultSettings={setSelectedDefaultSettings}
      isUpdateMode={isUpdateMode}
    />
  );
}

export default PlatformWorkspace;
